/* Only the journey film loops. Global pause, reduced motion and visibility win. */
(() => {
  'use strict';
  const init = () => {
    const video = document.querySelector('[data-pb-journey-loop]');
    if (!video || video.dataset.pbLoopReady) return;
    const frame = video.closest('.pb-original-film');
    const button = frame?.querySelector('[data-pb-journey-toggle]');
    if (!frame || !button) return;
    video.dataset.pbLoopReady = 'true';
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.loop = true;
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const root = document.documentElement;
    const otherFilms = [...document.querySelectorAll('[data-pb-user-film]')];
    const frameCallbacks = typeof video.requestVideoFrameCallback === 'function';
    let visible = false, userPaused = false, activePage = true;
    let playPending = false, autoplayRejected = false, failed = false, playAttempt = 0;
    let playbackStarted = false, frameVisible = false, frameRequest = 0, lastPlaybackTime = video.currentTime || 0;
    const blockedByMotion = () => reduced.matches || root.classList.contains('dm-paused');
    const eligible = () => !failed && visible && activePage && !document.hidden && !blockedByMotion() && !userPaused && !otherFilms.some(other => !other.paused && !other.ended);
    const syncButton = () => {
      const motionBlocked = blockedByMotion();
      // play()/the play event can precede decoded frames or hang while buffering.
      const playing = playbackStarted && frameVisible && !video.paused && !video.ended;
      const loading = !playing && (playPending || (!video.paused && !video.ended));
      button.disabled = motionBlocked || failed;
      button.textContent = failed ? 'Video unavailable' : motionBlocked ? 'Motion paused' : playing ? 'Pause video' : loading ? 'Loading video…' : 'Play video';
      button.setAttribute('aria-label', failed ? 'Journey video unavailable; photograph shown' : motionBlocked ? (reduced.matches ? 'Video paused for reduced motion' : 'Video paused by global motion control') : playing ? 'Pause journey video' : loading ? 'Pause loading journey video' : 'Play journey video');
      button.setAttribute('aria-pressed', String(playing));
    };
    const showPoster = () => {
      playbackStarted = false;
      frameVisible = false;
      frame.dataset.pbJourneyFrame = 'false';
      lastPlaybackTime = video.currentTime || 0;
      syncButton();
    };
    // Shot-specific square crops follow the verified source cut, without easing.
    const cropFrame = time => { video.style.objectPosition = time < 3.701681 ? '50% 20%' : '50% 90%'; };
    const updateFrame = time => {
      cropFrame(time);
      // rVFC mediaTime can lag currentTime. Use one clock for progress so a
      // delayed composited frame cannot make a stationary clock look advanced.
      const playbackTime = video.currentTime || 0;
      const advancing = playbackTime > lastPlaybackTime || (video.loop && lastPlaybackTime - playbackTime > .5);
      // Do not make poster removal depend solely on compositor callbacks.
      // Decoded, advancing media time is also evidence of resumed playback;
      // a network stalled event need not be followed by another playing event.
      if (!frameVisible && advancing && eligible() && !video.paused && !video.ended && !video.seeking && video.readyState >= 2) {
        playbackStarted = true;
        frameVisible = true;
        frame.dataset.pbJourneyFrame = 'true';
        syncButton();
      }
      lastPlaybackTime = playbackTime;
    };
    const nextFrame = () => {
      if (frameRequest || video.paused || !activePage || failed) return;
      frameRequest = frameCallbacks ? video.requestVideoFrameCallback((_now, metadata) => {
        frameRequest = 0;
        updateFrame(metadata.mediaTime);
        nextFrame();
      }) : requestAnimationFrame(() => {
        frameRequest = 0;
        updateFrame(video.currentTime || 0);
        nextFrame();
      });
    };
    const stopFrames = () => {
      if (frameRequest) {
        if (frameCallbacks) video.cancelVideoFrameCallback(frameRequest);
        else cancelAnimationFrame(frameRequest);
      }
      frameRequest = 0;
    };
    ['loadedmetadata','seeked'].forEach(type => video.addEventListener(type, () => cropFrame(video.currentTime || 0)));
    // Keep this fallback even when requestVideoFrameCallback exists.
    video.addEventListener('timeupdate', () => updateFrame(video.currentTime || 0));
    cropFrame(lastPlaybackTime);
    const playRejected = (error, attempt) => {
      if (attempt !== playAttempt) return;
      playPending = false;
      if (eligible() && error?.name !== 'AbortError') autoplayRejected = true;
      video.pause();
      showPoster();
    };
    const reconcile = () => {
      if (!eligible()) {
        // Ignore completion/rejection from playback we intentionally cancelled.
        if (playPending) { playPending = false; playAttempt++; }
        video.pause(); syncButton(); return;
      }
      if (!video.paused || playPending || autoplayRejected) { syncButton(); return; }
      video.muted = true;
      playPending = true;
      const attempt = ++playAttempt;
      showPoster();
      try {
        Promise.resolve(video.play()).then(() => {
          if (attempt !== playAttempt) return;
          playPending = false;
          if (!eligible()) video.pause();
          syncButton();
        }).catch(error => playRejected(error, attempt));
      } catch (error) { playRejected(error, attempt); }
    };
    button.addEventListener('click', () => {
      if (blockedByMotion() || failed) return;
      userPaused = !video.paused || playPending;
      autoplayRejected = false;
      // Manual play also works in browsers without IntersectionObserver.
      const rect = video.getBoundingClientRect();
      visible = rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
      reconcile();
    });
    video.addEventListener('play', () => {
      if (!eligible()) video.pause();
      nextFrame();
      syncButton();
    });
    video.addEventListener('playing', () => {
      if (!eligible()) { video.pause(); return; }
      playbackStarted = true;
      nextFrame();
      syncButton();
    });
    ['pause','ended'].forEach(type => video.addEventListener(type, () => { playbackStarted = false; stopFrames(); syncButton(); }));
    ['waiting','emptied'].forEach(type => video.addEventListener(type, showPoster));
    video.addEventListener('stalled', () => { if (video.readyState < 3) showPoster(); });
    const mediaFailed = () => { failed = true; playPending = false; playAttempt++; video.pause(); stopFrames(); showPoster(); };
    video.addEventListener('error', mediaFailed);
    video.querySelectorAll('source').forEach(source => source.addEventListener('error', mediaFailed));
    otherFilms.forEach(other => ['play','pause','ended'].forEach(type => other.addEventListener(type, reconcile)));
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (entry.target !== video) continue;
          visible = entry.isIntersecting && entry.intersectionRatio >= .25;
        }
        reconcile();
      }, {threshold:[0,.25]}).observe(video);
    }
    new MutationObserver(reconcile).observe(root,{attributes:true,attributeFilter:['class']});
    reduced.addEventListener('change', reconcile);
    document.addEventListener('visibilitychange', reconcile);
    window.addEventListener('pagehide', () => { activePage = false; reconcile(); showPoster(); });
    window.addEventListener('pageshow', () => { activePage = true; reconcile(); });
    showPoster();
    reconcile();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once:true}); else init();
})();
